import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import fr from './locales/fr.json';
import de from './locales/de.json';

const LOCALE_STORAGE_KEY = 'asyncagent_locale';
const LOCALE_PINNED_KEY = 'asyncagent_locale_pinned';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'fr', 'de'];

const MESSAGES = {
  en,
  fr,
  de
};

function normalizeLocale(rawLocale) {
  const normalized = String(rawLocale || '').trim().toLowerCase();
  if (!normalized) return '';
  const base = normalized.split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(base) ? base : '';
}

function readStoredLocale() {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function readLocalePinned() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(LOCALE_PINNED_KEY) === '1';
  } catch {
    return false;
  }
}

function detectNavigatorLocale() {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const candidates = Array.isArray(navigator.languages)
    ? navigator.languages
    : [navigator.language];
  for (const candidate of candidates) {
    const normalized = normalizeLocale(candidate);
    if (SUPPORTED_LOCALES.includes(normalized)) return normalized;
  }
  return DEFAULT_LOCALE;
}

function detectInitialLocale() {
  const storedRaw = readStoredLocale();
  const stored = normalizeLocale(storedRaw);
  const navigatorLocale = detectNavigatorLocale();
  const pinned = readLocalePinned();
  if (pinned && SUPPORTED_LOCALES.includes(stored)) return stored;
  if (SUPPORTED_LOCALES.includes(navigatorLocale)) return navigatorLocale;
  if (SUPPORTED_LOCALES.includes(stored)) return stored;
  return DEFAULT_LOCALE;
}

function readMessage(locale, key) {
  const root = MESSAGES[locale] || MESSAGES[DEFAULT_LOCALE] || {};
  const fallback = MESSAGES[DEFAULT_LOCALE] || {};
  const path = String(key || '').split('.');

  const resolvePath = (obj) => path.reduce((acc, segment) => (
    acc && typeof acc === 'object' && segment in acc ? acc[segment] : undefined
  ), obj);

  const direct = resolvePath(root);
  if (typeof direct === 'string') return direct;

  const fallbackValue = resolvePath(fallback);
  if (typeof fallbackValue === 'string') return fallbackValue;

  return key;
}

function interpolate(template, params) {
  if (typeof template !== 'string') return '';
  if (!params || typeof params !== 'object') return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, token) => {
    if (!(token in params)) return '';
    return String(params[token]);
  });
}

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key
});

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Best effort persistence only.
    }
  }, [locale]);

  const setLocale = useCallback((nextLocale) => {
    const normalized = normalizeLocale(nextLocale);
    const resolved = normalized || DEFAULT_LOCALE;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(LOCALE_PINNED_KEY, '1');
      } catch {
        // Best effort persistence only.
      }
    }
    setLocaleState(resolved);
  }, []);

  const t = useCallback((key, params) => {
    const message = readMessage(locale, key);
    return interpolate(message, params);
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
